import React, { useEffect, useState } from "react";
import { CheckCircle2, Download, Upload } from "lucide-react";
import { api, telecharger } from "./api";
import { Badge, BoutonSombre, Case, Eyebrow } from "./ui";
import { useConfirmation } from "./Confirmation";

/* Reprise de la configuration, le soir de la bascule : la production reçoit les DONNÉES
   d'Emergent (« Importer une sauvegarde »), mais ce qui a été RÉGLÉ seulement sur la
   version de test (fiches des profs, e-mails types, assistant, réglages des cours…)
   serait perdu.
   - Version de test : « Exporter la configuration (pour la production) » télécharge un
     fichier qui ne contient QUE de la configuration.
   - Production : « Reprendre la configuration du test… » → aperçu en lecture seule, section
     par section → on coche → « Appliquer ». Rien n'est effacé ; rejouer ne change rien. */

const ETATS = {
  nouveau: { ton: "vert", label: "NOUVEAU" },
  modifie: { ton: "bleu", label: "MODIFIÉ" },
  production_seule: { ton: "ambre", label: "GARDÉ" },
  inconnu: { ton: "ambre", label: "IGNORÉ" },
  refuse: { ton: "rouge", label: "REFUSÉ" },
};

const jour = (iso) => (iso ? `${String(iso).slice(8, 10)}.${String(iso).slice(5, 7)}.${String(iso).slice(0, 4)}` : "");

export default function ReglagesReprise() {
  const [etat, setEtat] = useState(null);
  useEffect(() => {
    api("/reprise-configuration/etat").then(setEtat).catch((e) => setEtat({ erreur: e.message }));
  }, []);
  if (!etat) return <p className="text-sm text-[var(--muted)]">Chargement…</p>;
  if (etat.erreur) return <p className="text-sm" style={{ color: "#B91C1C" }}>{etat.erreur}</p>;
  return etat.peut_exporter ? <Export etat={etat} /> : <Reprise etat={etat} />;
}

function Export({ etat }) {
  const { informer } = useConfirmation();
  const [enCours, setEnCours] = useState(false);
  const exporter = async () => {
    setEnCours(true);
    try {
      const d = new Date();
      const nom = `configuration-1000arts-TEST-${d.toISOString().slice(0, 10)}.json`;
      await telecharger("/reprise-configuration/export", nom);
    } catch (e) {
      await informer({ titre: "Export impossible", ton: "danger",
        texte: e.message === "Erreur 403" ? "Réservé à un compte administrateur." : e.message });
    }
    setEnCours(false);
  };
  return (
    <div className="bg-white rounded-2xl p-5 border space-y-3" style={{ borderColor: "var(--border)" }}
      data-testid="reprise-export">
      <p className="text-sm">
        Le fichier contient <strong>seulement la configuration</strong> : Réglages (tarifs,
        calendrier, stages, paramètres…), e-mails types, modèles d'envois groupés, assistant du
        site, fiches des profs (nom, e-mail, payé, tarif fixe), horaires partagés, annonces en
        cours et réglages des cours (lien WhatsApp, prof, places, exceptions…).
      </p>
      <p className="text-xs text-[var(--muted)]">
        Jamais une famille, un élève, une inscription, une facture, un paiement, une
        conversation de l'assistant ni un décompte de prof.
        {etat.copie_du && <> Copie d'Emergent du {jour(etat.copie_du)} : en production, seuls les
          réglages modifiés ici depuis cette date seront cochés d'office.</>}
      </p>
      <BoutonSombre onClick={exporter} disabled={enCours} testid="reprise-exporter">
        <Download size={14} /> {enCours ? "Préparation…" : "Exporter la configuration (pour la production)"}
      </BoutonSombre>
    </div>
  );
}

function Reprise({ etat }) {
  const { informer, demander } = useConfirmation();
  const [fichier, setFichier] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [choisis, setChoisis] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [fait, setFait] = useState(null);

  const lire = async (f) => {
    const corps = new FormData();
    corps.append("fichier", f);
    const a = await api("/reprise-configuration/apercu", { method: "POST", body: corps });
    setApercu(a);
    setChoisis(a.sections.flatMap((s) => s.items.filter((i) => i.coche).map((i) => i.id)));
    return a;
  };

  const choisir = async (evt) => {
    const f = evt.target.files?.[0];
    evt.target.value = "";
    if (!f) return;
    setEnCours(true);
    setFait(null);
    try {
      await lire(f);
      setFichier(f);
    } catch (e) {
      setApercu(null);
      await informer({ titre: "Fichier refusé", ton: "danger", texte: e.message });
    }
    setEnCours(false);
  };

  const appliquer = async () => {
    if (!await demander({
      titre: `Appliquer ${choisis.length} changement(s) ?`,
      texte: "Seuls les éléments cochés sont écrits dans la console de production. Rien n'est effacé.",
      points: ["Une ligne par section dans l'historique (Journal & comptes), avec l'avant et l'après.",
        "Rejouer le même fichier ne change rien."],
      valider: "Appliquer",
    })) return;
    setEnCours(true);
    try {
      const corps = new FormData();
      corps.append("fichier", fichier);
      corps.append("elements", JSON.stringify(choisis));
      const r = await api("/reprise-configuration/appliquer", { method: "POST", body: corps });
      setFait(r);
      await lire(fichier);
      await informer({
        titre: `${r.total} changement(s) appliqué(s)`, ton: "succes",
        texte: "La configuration cochée est maintenant celle de la production.",
        points: r.sections.filter((s) => s.ecrits).map((s) => `${s.titre} : ${s.ecrits}`),
      });
    } catch (e) {
      await informer({ titre: "Reprise impossible", ton: "danger", texte: e.message });
    }
    setEnCours(false);
  };

  const basculer = (id, oui) => setChoisis(oui ? [...new Set([...choisis, id])] : choisis.filter((x) => x !== id));

  return (
    <div className="bg-white rounded-2xl p-5 border space-y-4" style={{ borderColor: "var(--border)" }}
      data-testid="reprise-production">
      <p className="text-sm">
        Le soir de la bascule, <strong>juste après l'import de la sauvegarde d'Emergent</strong> :
        dépose ici le fichier exporté de la version de test. Tu vois d'abord ce qui changerait,
        rien n'est écrit avant « Appliquer ».
      </p>
      {etat.derniere_reprise && (
        <p className="text-xs text-[var(--muted)]" data-testid="reprise-derniere">
          Dernière reprise : {jour(etat.derniere_reprise.le)} ({etat.derniere_reprise.par || "—"}).
        </p>
      )}
      <label className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border cursor-pointer"
        style={{ borderColor: "var(--border)", opacity: enCours ? 0.5 : 1 }} data-testid="reprise-deposer">
        <Upload size={14} /> {enCours && !apercu ? "Lecture…" : "Reprendre la configuration du test…"}
        <input type="file" accept=".json,.gz" className="hidden" disabled={enCours} onChange={choisir} />
      </label>

      {apercu && (
        <div className="space-y-4" data-testid="reprise-apercu">
          <div className="rounded-2xl px-4 py-3 text-xs" style={{ background: "#EFF6FF", color: "#1D4ED8" }}>
            Export de la version de test du {jour(apercu.fichier.le)}{apercu.fichier.par ? ` (${apercu.fichier.par})` : ""}
            {apercu.fichier.copie_du ? ` — copie d'Emergent du ${jour(apercu.fichier.copie_du)}` : ""}.
            {" "}{apercu.a_appliquer
              ? `${apercu.a_appliquer} changement(s) possible(s), ${choisis.length} coché(s).`
              : "Rien à reprendre : la production a déjà toute la configuration de ce fichier."}
          </div>
          {fait && (
            <div className="rounded-2xl px-4 py-3 text-sm flex gap-2" data-testid="reprise-fait"
              style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }}>
              <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
              <span>{fait.total} changement(s) appliqué(s). Ce qui reste ci-dessous n'a pas été coché.</span>
            </div>
          )}
          {apercu.sections.map((s) => (
            <SectionApercu key={s.id} section={s} choisis={choisis} basculer={basculer}
              tout={(oui) => {
                const ids = s.items.filter((i) => i.applicable).map((i) => i.id);
                setChoisis(oui ? [...new Set([...choisis, ...ids])] : choisis.filter((x) => !ids.includes(x)));
              }} />
          ))}
          {apercu.a_appliquer > 0 && (
            /* Le bouton à GAUCHE : à droite, la bulle « Aide » le recouvrait. */
            <div className="sticky bottom-3 z-10 flex flex-wrap items-center gap-3 rounded-2xl px-4 py-3 border bg-white shadow"
              style={{ borderColor: "var(--border)" }}>
              <BoutonSombre onClick={appliquer} disabled={enCours || !choisis.length} testid="reprise-appliquer">
                {enCours ? "Écriture…" : `Appliquer (${choisis.length})`}
              </BoutonSombre>
              <span className="text-xs text-[var(--muted)]">{choisis.length} coché(s) sur {apercu.a_appliquer} possible(s)</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SectionApercu({ section, choisis, basculer, tout }) {
  const actifs = section.items.filter((i) => i.applicable);
  const autres = section.items.filter((i) => !i.applicable);
  const tousCoches = actifs.length > 0 && actifs.every((i) => choisis.includes(i.id));
  return (
    <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border)" }}
      data-testid={`reprise-section-${section.id}`}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3" style={{ background: "#F8FAFC" }}>
        {actifs.length > 0 && <Case cochee={tousCoches} onChange={tout} testid={`reprise-tout-${section.id}`}
          titre="Tout cocher / tout décocher" />}
        <div className="min-w-0">
          <div className="text-sm font-semibold">{section.titre}</div>
          <div className="text-[11px] text-[var(--muted)]">{section.aide}</div>
        </div>
        <span className="ml-auto text-[11px] text-[var(--muted)]">
          {actifs.length ? `${actifs.length} à reprendre` : "rien à reprendre"}
          {section.identiques ? ` · ${section.identiques} identique(s)` : ""}
        </span>
      </div>
      {section.items.length > 0 && (
        <ul className="divide-y" style={{ borderColor: "var(--border-soft)" }}>
          {[...actifs, ...autres].map((i) => (
            <Ligne key={i.id} item={i} cochee={choisis.includes(i.id)} basculer={basculer} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Ligne({ item, cochee, basculer }) {
  const e = ETATS[item.etat] || ETATS.modifie;
  return (
    <li className="px-4 py-3 text-sm flex gap-3 items-start" data-testid="reprise-ligne"
      style={{ opacity: item.applicable ? 1 : 0.75 }}>
      <div className="pt-0.5 w-[18px] shrink-0">
        {item.applicable && <Case cochee={cochee} onChange={(v) => basculer(item.id, v)} testid="reprise-case" />}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge ton={e.ton}>{e.label}</Badge>
          <span className="font-medium break-words">{item.libelle}</span>
        </div>
        {(item.avant !== "—" || item.apres !== "—") && item.etat !== "inconnu" && (
          <div className="text-xs break-words">
            <span className="text-[var(--muted)]">{item.avant}</span>
            {item.apres !== "—" && <> <span className="text-[var(--muted)]">→</span> <strong>{item.apres}</strong></>}
          </div>
        )}
        {item.details?.length > 0 && (
          <ul className="text-xs text-[var(--muted)] space-y-0.5">
            {item.details.map((d, k) => <li key={k} className="break-words">· {d}</li>)}
          </ul>
        )}
        {item.note && (
          <div className="text-xs rounded-lg px-2 py-1 inline-block"
            style={item.applicable && !item.coche ? { background: "#FFFBEB", color: "#92400E" }
              : { background: "#F1F5F9", color: "#475569" }}>
            {item.note}
          </div>
        )}
        {item.texte_apres !== undefined && (
          <details className="text-xs">
            <summary className="cursor-pointer text-[var(--muted)]">Voir le texte</summary>
            <div className="grid md:grid-cols-2 gap-2 mt-2">
              <div>
                <Eyebrow>Production maintenant</Eyebrow>
                <pre className="whitespace-pre-wrap break-words rounded-lg p-2 mt-1 max-h-64 overflow-auto"
                  style={{ background: "#F8FAFC" }}>{item.texte_avant || "—"}</pre>
              </div>
              <div>
                <Eyebrow>Fichier du test</Eyebrow>
                <pre className="whitespace-pre-wrap break-words rounded-lg p-2 mt-1 max-h-64 overflow-auto"
                  style={{ background: "#F0FDF4" }}>{item.texte_apres || "—"}</pre>
              </div>
            </div>
          </details>
        )}
      </div>
    </li>
  );
}
